import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { stringify } from 'query-string'

import './styles.scss'
import SearchFilters from '../../kit/modules/filter-search'
import { Button } from '../../kit/components/button'
import ENDPOINT_ROUTES from '../../constants/APIs'
import { ROUTE_NAMES } from '../../constants/route-names'
import { FeatureEnabledState, getFeatureEnabled, withFeatureEnabled } from '../../contexts/FeatureContext'
import BulkRegistrationEventsTable from './BulkRegistrationEventsTable'
import { isRowSelectable, mapDailyEvents } from './helpers/mapDailyEvents'
import { BulkEventTableRow, OpportunityEvent } from './types'

interface MatchParams {
  organizationId: string
}

interface Props extends RouteComponentProps<MatchParams> {
  getJSON?: (api: string) => Promise<any>
  postJSON?: (api: string, data: object) => Promise<any>
  currentUser?: any
  featureEnabledState?: FeatureEnabledState
  setOpenLogin?: (isOpenLogin: boolean) => void
}

const defaultDate = {
  value: {
    from: moment().day(0).format('L'),
    to: moment().day(6).format('L'),
  },
  label: 'This week',
}

const VolunteerBulkRegistrationScene = ({
  match,
  getJSON,
  postJSON,
  currentUser,
  featureEnabledState,
  setOpenLogin,
}: Props) => {
  const orgId = match.params.organizationId
  const [date, setDate] = useState(defaultDate)
  const [searchInput, setSearchInput] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [events, setEvents] = useState<OpportunityEvent[]>([])
  const [tableLoading, setTableLoading] = useState(true)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set())
  const [registering, setRegistering] = useState(false)
  const pendingRegisterAfterLoginRef = useRef(false)
  const [registerMessage, setRegisterMessage] = useState<{
    type: 'success' | 'error' | 'warning'
    text: string
  } | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchValue(searchInput), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const handleDateChange = (
    options: typeof defaultDate | { label: string; value: 'customDate' | typeof defaultDate.value },
  ) => {
    if (options.value === 'customDate') {
      return
    }
    setDate(options as typeof defaultDate)
  }

  const rows = useMemo(() => mapDailyEvents(events), [events])

  const selectableRowByKey = useMemo(() => {
    const lookup = new Map<string, BulkEventTableRow>()

    rows.forEach(dayRow => {
      ;(dayRow.subRows || []).filter(isRowSelectable).forEach(row => {
        lookup.set(row.rowKey, row)
      })
    })

    return lookup
  }, [rows])

  const getEventsList = useCallback(
    async (options?: { background?: boolean }) => {
      if (!getJSON) {
        return
      }

      if (!options?.background) {
        setTableLoading(true)
      }

      const now = moment()
      const query: Record<string, string | boolean> = {
        includeMissingRequiredMemberships: true,
        status: 'any',
        startDate: now.format(),
        endDate: moment().add(5, 'years').endOf('day').format(),
      }

      if (date.value.from && date.value.to) {
        const rangeStart = moment(date.value.from, 'L').startOf('day')
        const rangeEnd = moment(date.value.to, 'L').endOf('day')
        query.startDate = moment.max(rangeStart, now).format()
        query.endDate = rangeEnd.format()
      }

      const apiUrl = ENDPOINT_ROUTES.Events.getOccurrenceRefactorEvents(orgId, stringify(query))

      try {
        const response = await getJSON(apiUrl)
        const cutoff = moment()
        const upcomingEvents = (response?.list || []).filter((event: OpportunityEvent) =>
          moment(event.endsAt || event.startsAt).isSameOrAfter(cutoff),
        )
        setEvents(upcomingEvents)
        setSelectedKeys(new Set())
      } finally {
        if (!options?.background) {
          setTableLoading(false)
        }
      }
    },
    [date.value.from, date.value.to, getJSON, orgId],
  )

  const handleSearchAction = useCallback(() => {
    void getEventsList()
  }, [getEventsList])

  useEffect(() => {
    getEventsList()
  }, [getEventsList])

  useEffect(() => {
    if (currentUser?.sub) {
      getEventsList()
    }
  }, [currentUser, getEventsList])

  const handleRegister = useCallback(async () => {
    if (!selectedKeys.size || !postJSON) {
      return
    }

    if (!currentUser?.sub) {
      pendingRegisterAfterLoginRef.current = true
      setOpenLogin?.(true)
      return
    }

    setRegistering(true)
    setRegisterMessage(null)

    try {
      const registrations = Array.from(selectedKeys)
        .map(rowKey => selectableRowByKey.get(rowKey))
        .filter((row): row is BulkEventTableRow => !!row?.encodedOccurrence && !!row.roleId && !!row.shiftId)
        .map(row => ({
          encodedOccurrence: row.encodedOccurrence as string,
          roleId: row.roleId as number,
          timeshiftId: row.shiftId as number,
        }))

      const response = await postJSON(ENDPOINT_ROUTES.EventRegistration.bulkSelfRegister(), {
        registrations,
      })

      if (response?.statusCode === 401) {
        pendingRegisterAfterLoginRef.current = true
        setOpenLogin?.(true)
        return
      }

      const succeededCount = response?.succeeded?.length || 0
      const failedItems: Array<{ error?: string }> = response?.failed || []
      const failedCount = failedItems.length
      const failedDetails = failedItems
        .map(item => item.error)
        .filter(Boolean)
        .join(' ')

      if (failedCount > 0) {
        setRegisterMessage({
          type: 'error',
          text: succeededCount
            ? `Registered ${succeededCount} shift(s). ${failedDetails || `${failedCount} failed.`}`
            : failedDetails || `${failedCount} registration(s) failed.`,
        })
      } else if (succeededCount > 0) {
        setRegisterMessage({
          type: 'success',
          text: `Registered ${succeededCount} shift(s).`,
        })
      } else {
        setRegisterMessage({
          type: 'error',
          text: 'Registration failed. Please try again.',
        })
      }

      await getEventsList({ background: true })
    } catch (error) {
      setRegisterMessage({
        type: 'error',
        text: 'Registration failed. Please try again.',
      })
      console.error(error)
    } finally {
      setRegistering(false)
    }
  }, [currentUser, getEventsList, postJSON, selectableRowByKey, selectedKeys, setOpenLogin])

  useEffect(() => {
    if (!currentUser?.sub || !pendingRegisterAfterLoginRef.current) {
      return
    }

    pendingRegisterAfterLoginRef.current = false
    void handleRegister()
  }, [currentUser, handleRegister])

  const toggleSelectedKey = useCallback((rowKey: string) => {
    setSelectedKeys(current => {
      const next = new Set(current)
      if (next.has(rowKey)) {
        next.delete(rowKey)
      } else {
        next.add(rowKey)
      }
      return next
    })
  }, [])

  if (!getFeatureEnabled(featureEnabledState as FeatureEnabledState, 'VolunteerBulkRegistration')) {
    return <Redirect to={ROUTE_NAMES.allEvents(orgId)} />
  }

  return (
    <div className="bulk-registration-page">
      <div className="bulk-registration-header">
        <div>
          <h1 className="bulk-registration-header__title">All Events</h1>
          <p className="bulk-registration-header__subtitle">
            Select shifts across multiple events and register in one step.
          </p>
        </div>
        <Button
          label={registering ? 'Registering…' : 'Register'}
          type="secondary"
          labelColor="#FFFFFF"
          width="160px"
          height="48px"
          onClick={handleRegister}
          style={{
            opacity: !selectedKeys.size && !registering ? 0.55 : 1,
            pointerEvents: !selectedKeys.size || registering ? 'none' : 'auto',
          }}
        />
      </div>

      <div className="bulk-registration-toolbar">
        <div className="bulk-registration-toolbar__filters">
          <SearchFilters
            title=""
            hideInputSearch
            hideResetButton
            dateValue={date}
            onChangeDate={handleDateChange}
            onSearchAction={handleSearchAction}
          />
        </div>
        <div className="bulk-registration-toolbar__search">
          <input
            type="search"
            placeholder="Search events, roles, shifts"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
          />
        </div>
      </div>

      <div className="bulk-registration-selection-count">
        {selectedKeys.size ? `${selectedKeys.size} shift(s) selected` : 'No shifts selected'}
      </div>

      {registerMessage && (
        <div className={`bulk-registration-message bulk-registration-message--${registerMessage.type}`} role="status">
          {registerMessage.text}
        </div>
      )}

      <div className="bulk-registration-table-section">
        <BulkRegistrationEventsTable
          rows={rows}
          orgId={orgId}
          selectedKeys={selectedKeys}
          onToggleKey={toggleSelectedKey}
          onSelectionKeysChange={setSelectedKeys}
          searchValue={searchValue}
          loading={tableLoading}
        />
        {tableLoading && (
          <div className="bulk-registration-table-overlay" aria-hidden="true">
            <div className="bulk-registration-table-spinner" />
          </div>
        )}
      </div>
    </div>
  )
}

export default withFeatureEnabled(VolunteerBulkRegistrationScene)
